<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useCopilotWidget } from '@/composables/useCopilotWidget'
import { getInitials } from '@/utils/formatters'
import {
  LayoutDashboard,
  Settings,
  Users,
  Building2,
  FolderKanban,
  FileText,
  Ticket,
  Calendar,
  TrendingUp,
  DollarSign,
  Rocket,
  Flame,
  MessageSquare,
  MessageSquarePlus,
  History,
  LogOut,
  BotMessageSquare,
  KanbanSquare,
  AppWindow,
  Boxes,
  Shield,
  UserPlus,
  Terminal,
  Network,
  Code2,
  GitBranch,
  PenLine,
  Plug,
  CheckSquare,
  MessageSquareHeart,
  ClipboardList,
  SquareTerminal,
  Menu,
  X,
} from 'lucide-vue-next'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

interface MenuItem {
  title: string
  url: string
  icon: Component
  roles?: string[]
}

interface MenuGroup {
  label?: string
  items: MenuItem[]
}

interface ModuleSection {
  id: string
  label: string
  icon: Component
  items: MenuItem[]
  groups?: MenuGroup[]
  directUrl?: string
}

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const {
  user,
  logout,
  currentTenant,
  isSuperAdmin,
  isAdmin,
  isOperator,
  isClient,
  isMember,
  isModuleEnabled,
} = useAuth()

const { open: openCopilot } = useCopilotWidget()

const activeModule = ref<string | null>(null)
const isMobileMenuOpen = ref(false)
const drawerRef = ref<HTMLElement | null>(null)
const railRef = ref<HTMLElement | null>(null)

// Navigation items with role restrictions
const commercialItems = computed<MenuItem[]>(() => [
  { title: t('navigation.pipeline'), url: '/crm/pipeline', icon: TrendingUp, roles: ['admin'] },
  { title: t('navigation.prospects'), url: '/crm/prospects', icon: Users, roles: ['admin'] },
  { title: t('navigation.quotations'), url: '/quotations', icon: MessageSquare, roles: ['admin', 'client', 'member'] },
  { title: t('navigation.salesOrders'), url: '/sales-orders', icon: DollarSign, roles: ['admin'] },
  { title: 'Ignite', url: '/ignite', icon: Flame, roles: ['superadmin'] },
])

// Project items split into groups for the drawer
const projectGeneralItems = computed<MenuItem[]>(() => [
  { title: t('navigation.organizations'), url: '/organizations', icon: Building2, roles: ['admin', 'operator'] },
  { title: t('navigation.myOrganization'), url: '/my-organization', icon: Building2, roles: ['client'] },
  { title: t('navigation.projects'), url: '/projects', icon: FolderKanban },
  { title: 'Entregables', url: '/deliverables', icon: Boxes },
])

const projectDevItems = computed<MenuItem[]>(() => [
  { title: t('navigation.applications'), url: '/applications', icon: AppWindow },
  { title: t('navigation.requirements'), url: '/requirements', icon: FileText },
  { title: t('navigation.releases'), url: '/releases', icon: Rocket, roles: ['admin', 'operator'] },
  { title: t('navigation.backlog'), url: '/backlog', icon: KanbanSquare, roles: ['admin', 'operator'] },
  { title: t('navigation.implementations'), url: '/implementations', icon: Rocket, roles: ['superadmin', 'admin', 'operator'] },
])

// Flat list for module visibility check
const projectItems = computed<MenuItem[]>(() => [
  ...projectGeneralItems.value,
  ...projectDevItems.value,
])

const supportItems = computed<MenuItem[]>(() => [
  { title: t('navigation.petitions'), url: '/petitions', icon: MessageSquarePlus },
  { title: t('navigation.tickets'), url: '/tickets', icon: Ticket },
  { title: 'Calendario', url: '/calendar', icon: Calendar },
])

const adminItems = computed<MenuItem[]>(() => [
  { title: t('navigation.users'), url: '/users', icon: Users, roles: ['admin'] },
  { title: t('navigation.activityLog'), url: '/activity-log', icon: History, roles: ['superadmin'] },
  { title: t('navigation.settings'), url: '/settings', icon: Settings, roles: ['admin'] },
])

// Dev Center — herramientas internas de desarrollo (espeja el grupo del mobile).
// Gateado por el módulo `devCenter` del tenant + rol admin; el superadmin ve todo.
const devCenterItems = computed<MenuItem[]>(() => [
  { title: 'Grafo de conocimiento', url: '/dev-center/knowledge-graph', icon: Network },
  { title: 'Grafo de código', url: '/dev-center/codegraph', icon: Code2 },
  // TPL-94: dos entradas separadas (paridad con el drawer del mobile) — terminal V1 (xterm) vs
  // sesiones de agente V2 (chat continuable). Cada una es su propia vista/lista.
  { title: 'Sesiones de terminal', url: '/dev-center/sessions', icon: Terminal },
  { title: 'Sesiones de agente', url: '/dev-center/agent-sessions', icon: BotMessageSquare },
  { title: 'Pipelines', url: '/dev-center/pipelines', icon: GitBranch },
])

const superAdminItems = computed<MenuItem[]>(() => [
  { title: 'Dashboard', url: '/super-admin', icon: Shield, roles: ['superadmin'] },
  { title: 'Tenants', url: '/super-admin/tenants', icon: Building2, roles: ['superadmin'] },
  { title: 'Leads', url: '/leads', icon: UserPlus, roles: ['superadmin'] },
  // Sessions y Grafo de conocimiento se movieron al grupo "Dev Center" (arriba).
  { title: 'Blog', url: '/super-admin/blog', icon: PenLine, roles: ['superadmin'] },
  { title: 'Feedback', url: '/super-admin/feedback', icon: MessageSquareHeart, roles: ['superadmin'] },
])

const filterByRole = (items: MenuItem[]): MenuItem[] => {
  const userRole = user.value?.role
  if (!userRole) return []
  return items.filter((item) => {
    if (!item.roles) return true
    return item.roles.includes(userRole)
  })
}

const isCommercialEnabled = computed(() => isModuleEnabled.value('commercial'))

const modules = computed<ModuleSection[]>(() => {
  const sections: ModuleSection[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      items: [],
      directUrl: '/dashboard',
    },
  ]

  // Copilot as direct action
  if (isSuperAdmin.value || ((isAdmin.value || isOperator.value) && isModuleEnabled.value('agent'))) {
    sections.push({
      id: 'copilot',
      label: 'Copilot',
      icon: BotMessageSquare,
      items: [],
    })
  }

  // Dev Center: justo debajo de Copilot. Visible para el superadmin (ve todo) o para
  // admins con el módulo `devCenter` activo en su tenant.
  if (isSuperAdmin.value || (isAdmin.value && isModuleEnabled.value('devCenter'))) {
    sections.push({
      id: 'devcenter',
      label: 'Dev Center',
      icon: SquareTerminal,
      items: devCenterItems.value,
    })
  }

  const filteredCommercial = filterByRole(commercialItems.value)
  if (isCommercialEnabled.value && filteredCommercial.length > 0) {
    sections.push({
      id: 'commercial',
      label: t('navigation.commercial'),
      icon: TrendingUp,
      items: filteredCommercial,
    })
  }

  const filteredProjectGeneral = filterByRole(projectGeneralItems.value)
  const filteredProjectDev = filterByRole(projectDevItems.value)
  const allProjectItems = [...filteredProjectGeneral, ...filteredProjectDev]
  if (allProjectItems.length > 0) {
    const projectGroups: MenuGroup[] = []
    if (filteredProjectGeneral.length > 0) {
      projectGroups.push({ items: filteredProjectGeneral })
    }
    if (filteredProjectDev.length > 0) {
      projectGroups.push({ label: 'Software', items: filteredProjectDev })
    }
    sections.push({
      id: 'projects',
      label: t('navigation.projects'),
      icon: FolderKanban,
      items: allProjectItems,
      groups: projectGroups,
    })
  }

  // Tareas as direct link (admin/operator only)
  if (isAdmin.value || isOperator.value) {
    sections.push({
      id: 'tasks',
      label: 'Tareas',
      icon: CheckSquare,
      items: [],
      directUrl: '/tasks',
    })
  }

  const filteredSupport = filterByRole(supportItems.value)
  if (filteredSupport.length > 0) {
    sections.push({
      id: 'management',
      label: t('navigation.management'),
      icon: ClipboardList,
      items: filteredSupport,
    })
  }

  // Integraciones as direct link
  if (isAdmin.value || isOperator.value) {
    sections.push({
      id: 'integrations',
      label: 'Integraciones',
      icon: Plug,
      items: [],
      directUrl: '/integrations',
    })
  }

  const filteredAdmin = filterByRole(adminItems.value)
  if (filteredAdmin.length > 0) {
    sections.push({
      id: 'admin',
      label: t('navigation.settings'),
      icon: Settings,
      items: filteredAdmin,
    })
  }

  const filteredSuperAdmin = filterByRole(superAdminItems.value)
  if (filteredSuperAdmin.length > 0) {
    sections.push({
      id: 'superadmin',
      label: 'SuperAdmin',
      icon: Shield,
      items: filteredSuperAdmin,
    })
  }

  return sections
})

const isActive = (url: string): boolean => {
  return route.path === url || route.path.startsWith(url + '/')
}

const isModuleActive = (mod: ModuleSection): boolean => {
  if (mod.directUrl) return isActive(mod.directUrl)
  return mod.items.some((item) => isActive(item.url))
}

const handleModuleClick = (mod: ModuleSection) => {
  if (mod.id === 'copilot') {
    openCopilot()
    activeModule.value = null
    return
  }

  if (mod.directUrl) {
    router.push(mod.directUrl)
    activeModule.value = null
    return
  }

  if (activeModule.value === mod.id) {
    activeModule.value = null
  } else {
    activeModule.value = mod.id
  }
}

const handleItemClick = (url: string) => {
  router.push(url)
  activeModule.value = null
  isMobileMenuOpen.value = false
}

const handleLogout = async () => {
  await logout()
}

const roleLabel = computed(() => {
  if (isSuperAdmin.value) return 'SuperAdmin'
  if (isAdmin.value) return 'Admin'
  if (isOperator.value) return 'Operador'
  if (isClient.value) return 'Cliente'
  if (isMember.value) return 'Miembro'
  return ''
})

const activeModuleData = computed(() => {
  if (!activeModule.value) return null
  return modules.value.find((m) => m.id === activeModule.value) || null
})

// Close drawer on click outside
const handleClickOutside = (e: MouseEvent) => {
  if (!activeModule.value) return
  const target = e.target as HTMLElement
  if (
    drawerRef.value && !drawerRef.value.contains(target) &&
    railRef.value && !railRef.value.contains(target)
  ) {
    activeModule.value = null
  }
}

// Close drawer on route change
const stopRouteWatch = router.afterEach(() => {
  activeModule.value = null
})

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
  stopRouteWatch()
})
</script>

<template>
  <!-- Mobile hamburger trigger -->
  <button
    class="fixed top-4 left-4 z-50 flex md:hidden items-center justify-center h-9 w-9 rounded-md border bg-background text-foreground shadow-sm"
    @click="isMobileMenuOpen = !isMobileMenuOpen"
  >
    <Menu v-if="!isMobileMenuOpen" class="h-5 w-5" />
    <X v-else class="h-5 w-5" />
  </button>

  <!-- Mobile overlay -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isMobileMenuOpen"
        class="fixed inset-0 z-40 bg-black/50 md:hidden"
        @click="isMobileMenuOpen = false"
      />
    </Transition>
  </Teleport>

  <!-- Mobile full menu -->
  <Teleport to="body">
    <Transition name="slide-left">
      <div
        v-if="isMobileMenuOpen"
        class="fixed inset-y-0 left-0 z-50 w-72 bg-sidebar border-r border-sidebar-border shadow-xl md:hidden overflow-y-auto"
      >
        <!-- Mobile header -->
        <div class="flex items-center gap-3 p-4 border-b border-sidebar-border">
          <div class="flex aspect-square size-8 items-center justify-center rounded-lg overflow-hidden">
            <img src="/img/icons/isotipoblack.svg" alt="Torcaza" class="size-8 dark:hidden" />
            <img src="/img/icons/isotipowhite.svg" alt="Torcaza" class="size-8 hidden dark:block bg-primary rounded-lg p-0.5" />
          </div>
          <div class="flex flex-col gap-0.5 leading-none">
            <span class="font-semibold text-sidebar-foreground">Torcaza Projects</span>
            <span class="text-xs text-muted-foreground">{{ roleLabel }}</span>
          </div>
        </div>

        <!-- Mobile menu sections -->
        <nav class="p-2">
          <template v-for="mod in modules" :key="mod.id">
            <!-- Direct link modules -->
            <template v-if="mod.directUrl || mod.id === 'copilot'">
              <button
                class="flex items-center gap-3 w-full rounded-md px-3 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                :class="{ 'bg-sidebar-accent font-medium': mod.directUrl && isActive(mod.directUrl) }"
                @click="mod.id === 'copilot' ? openCopilot() : handleItemClick(mod.directUrl!)"
              >
                <component :is="mod.icon" class="h-5 w-5 shrink-0" />
                <span>{{ mod.label }}</span>
              </button>
            </template>

            <!-- Section with items -->
            <template v-else>
              <div class="mt-3 mb-1 px-3">
                <span class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{{ mod.label }}</span>
              </div>
              <button
                v-for="item in mod.items"
                :key="item.url"
                class="flex items-center gap-3 w-full rounded-md px-3 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
                :class="{ 'bg-sidebar-accent font-medium text-primary': isActive(item.url) }"
                @click="handleItemClick(item.url)"
              >
                <component :is="item.icon" class="h-4 w-4 shrink-0" />
                <span>{{ item.title }}</span>
              </button>
            </template>
          </template>
        </nav>

        <!-- Mobile footer -->
        <div class="absolute bottom-0 left-0 right-0 border-t border-sidebar-border p-3 bg-sidebar">
          <div class="flex items-center gap-3">
            <Avatar class="h-8 w-8">
              <AvatarImage v-if="user?.avatarUrl" :src="user.avatarUrl" />
              <AvatarFallback class="text-xs">{{ getInitials(user?.displayName) }}</AvatarFallback>
            </Avatar>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-sidebar-foreground truncate">{{ user?.displayName }}</p>
              <p class="text-xs text-muted-foreground truncate">{{ user?.email }}</p>
            </div>
            <button @click="handleLogout" class="text-muted-foreground hover:text-destructive transition-colors">
              <LogOut class="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Desktop: Icon Rail (Google Ads style) -->
  <div
    ref="railRef"
    class="hidden md:flex fixed inset-y-0 left-0 z-30 w-[88px] flex-col bg-sidebar border-r border-sidebar-border"
  >
    <!-- Logo -->
    <div class="flex items-center justify-center h-14 shrink-0">
      <RouterLink to="/dashboard" class="flex items-center justify-center">
        <div class="flex aspect-square size-9 items-center justify-center rounded-lg overflow-hidden">
          <img src="/img/icons/isotipoblack.svg" alt="Torcaza" class="size-9 dark:hidden" />
          <img src="/img/icons/isotipowhite.svg" alt="Torcaza" class="size-9 hidden dark:block bg-primary rounded-lg p-0.5" />
        </div>
      </RouterLink>
    </div>

    <!-- Module icons — top aligned like Google Ads -->
    <nav class="flex-1 flex flex-col items-center gap-1 px-2 pt-2 overflow-y-auto scrollbar-none">
      <template v-for="mod in modules" :key="mod.id">
        <button
          class="rail-module group relative flex flex-col items-center justify-center w-full rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
          :class="{
            'rail-module--active !text-primary': activeModule === mod.id || (mod.directUrl && isActive(mod.directUrl)) || (!mod.directUrl && mod.id !== 'copilot' && isModuleActive(mod)),
            'rail-module--open !bg-primary/10': activeModule === mod.id,
          }"
          @click="handleModuleClick(mod)"
        >
          <!-- Active indicator bar on left edge of rail -->
          <div
            v-if="activeModule === mod.id || (mod.directUrl && isActive(mod.directUrl)) || (!mod.directUrl && mod.id !== 'copilot' && isModuleActive(mod))"
            class="absolute -left-2 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full bg-primary transition-all duration-200"
            :class="activeModule === mod.id ? 'h-7' : 'h-4'"
          />
          <component :is="mod.icon" class="h-5 w-5 shrink-0" :stroke-width="1.75" />
          <span class="text-[10px] leading-tight mt-1.5 w-full text-center truncate">{{ mod.label }}</span>
        </button>
      </template>
    </nav>

    <!-- User avatar at bottom -->
    <div class="flex flex-col items-center py-4 border-t border-sidebar-border shrink-0">
      <RouterLink to="/profile" class="block group">
        <Avatar class="h-8 w-8 ring-2 ring-transparent group-hover:ring-primary/20 transition-all">
          <AvatarImage v-if="user?.avatarUrl" :src="user.avatarUrl" />
          <AvatarFallback class="text-[10px] font-medium">{{ getInitials(user?.displayName) }}</AvatarFallback>
        </Avatar>
      </RouterLink>
    </div>
  </div>

  <!-- Desktop: Drawer panel -->
  <Transition name="slide-drawer">
    <div
      v-if="activeModuleData && activeModuleData.items.length > 0"
      ref="drawerRef"
      class="hidden md:flex fixed inset-y-0 left-[88px] z-20 w-60 flex-col bg-sidebar border-r border-sidebar-border shadow-lg"
    >
      <!-- Drawer header -->
      <div class="flex items-center justify-between h-14 px-4 border-b border-sidebar-border shrink-0">
        <h2 class="text-sm font-semibold text-sidebar-foreground">{{ activeModuleData.label }}</h2>
        <button
          class="text-muted-foreground hover:text-sidebar-foreground transition-colors"
          @click="activeModule = null"
        >
          <X class="h-4 w-4" />
        </button>
      </div>

      <!-- Drawer items -->
      <nav class="flex-1 overflow-y-auto p-2">
        <!-- Grouped layout -->
        <template v-if="activeModuleData.groups?.length">
          <template v-for="(group, gi) in activeModuleData.groups" :key="gi">
            <div v-if="gi > 0" class="my-1.5 mx-2 border-t border-sidebar-border" />
            <p v-if="group.label" class="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {{ group.label }}
            </p>
            <button
              v-for="item in group.items"
              :key="item.url"
              class="flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
              :class="{ 'bg-primary/10 text-primary font-medium': isActive(item.url) }"
              @click="handleItemClick(item.url)"
            >
              <component :is="item.icon" class="h-4 w-4 shrink-0" />
              <span>{{ item.title }}</span>
            </button>
          </template>
        </template>

        <!-- Flat layout (no groups) -->
        <template v-else>
          <button
            v-for="item in activeModuleData.items"
            :key="item.url"
            class="flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
            :class="{ 'bg-primary/10 text-primary font-medium': isActive(item.url) }"
            @click="handleItemClick(item.url)"
          >
            <component :is="item.icon" class="h-4 w-4 shrink-0" />
            <span>{{ item.title }}</span>
          </button>
        </template>
      </nav>

      <!-- Tenant info -->
      <div v-if="currentTenant?.name" class="px-4 py-3 border-t border-sidebar-border">
        <p class="text-xs text-muted-foreground truncate">{{ currentTenant.name }}</p>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Fade transition for mobile overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide left for mobile menu */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.25s ease;
}
.slide-left-enter-from,
.slide-left-leave-to {
  transform: translateX(-100%);
}

/* Slide drawer for desktop panel */
.slide-drawer-enter-active,
.slide-drawer-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.slide-drawer-enter-from,
.slide-drawer-leave-to {
  transform: translateX(-16px);
  opacity: 0;
}

/* Hide scrollbar on rail */
.scrollbar-none {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-none::-webkit-scrollbar {
  display: none;
}

/* Rail module buttons */
.rail-module {
  padding-top: 10px;
  padding-bottom: 8px;
}
.rail-module--active {
  background-color: hsl(var(--primary) / 0.08);
}
.rail-module--open {
  background-color: hsl(var(--primary) / 0.12);
}
</style>
